import React, {useCallback} from 'react'
import {useSelector} from 'react-redux'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {
  getDevicePlatform,
  getDeviceType,
  getLanguageCode
} from 'modules/meta/selectors'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {Placement} from '@vio/core/src/deepLinking/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {
  AppleAppStoreBadge,
  GooglePlayBadge
} from '@vio/shared/src/search/footer/AppButtonsWidget/AppButtonsWidget'

const AppStoreLink = ({
  onClick,
  getHref,
  children,
  dataId
}: {
  onClick: React.MouseEventHandler<HTMLAnchorElement>
  getHref: () => string
  children: React.ReactNode
  dataId: string
}) => {
  const isHydrated = useIsHydrated()
  if (isHydrated) {
    return (
      <NoStyleLink onClick={onClick} href={getHref()} data-id={dataId}>
        {children}
      </NoStyleLink>
    )
  } else {
    return <span data-id={dataId}>{children}</span>
  }
}

const AppStoreButton = ({
  onClick,
  getHref,
  dataId
}: {
  onClick: React.MouseEventHandler<HTMLAnchorElement>
  getHref: () => string
  children: React.ReactNode
  dataId: string
}) => {
  const isHydrated = useIsHydrated()

  return (
    <Button
      onClick={onClick}
      href={isHydrated ? getHref() : undefined}
      data-id={dataId}
      variant="primary"
      size="lg"
      fullWidth
    >
      <FormattedMessageWrapper
        id="switchToTheApp"
        defaultMessage="Switch to the app"
      />
    </Button>
  )
}

export const GetTheAppButtons = ({
  alwaysShowButtons = false,
  isButtonVariant = false,
  placement,
  redirectLanguage
}: {
  alwaysShowButtons?: boolean
  isButtonVariant?: boolean
  placement: Placement
  redirectLanguage?: string
}) => {
  const deviceType = useSelector(getDeviceType)
  const devicePlatform = useSelector(getDevicePlatform)
  const lang = useSelector(getLanguageCode) as string
  const isHydrated = useIsHydrated()
  const showButtons = alwaysShowButtons || deviceType === 'desktop'
  const {generateAppStoreDeepLink, generatePlayStoreDeepLink} =
    useGenerateAppDownloadUrl()
  const getAppStoreDownloadUrl = useCallback(
    () => generateAppStoreDeepLink(placement, undefined, redirectLanguage),
    [generateAppStoreDeepLink, placement, redirectLanguage]
  )
  const getPlayStoreDownloadUrl = useCallback(
    () => generatePlayStoreDeepLink(placement, undefined, redirectLanguage),
    [generatePlayStoreDeepLink, placement, redirectLanguage]
  )

  const handleGetAppClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault()

    const targetElement = event.target as HTMLElement
    const link = targetElement.closest('[data-id]') as HTMLLinkElement
    const {href} = link

    window.open(href, '_blank')
  }, [])

  // We do not have devicePlatform on the server yet
  const showIosButton = showButtons || (devicePlatform === 'iOS' && isHydrated)
  const showAndroidButton =
    showButtons || (devicePlatform === 'android' && isHydrated)

  const AppStoreButtonComponent = isButtonVariant
    ? AppStoreButton
    : AppStoreLink

  return (
    <Box gap="s500" width={isButtonVariant ? '100%' : 'auto'} display="flex">
      {showIosButton && (
        <AppStoreButtonComponent
          onClick={handleGetAppClick}
          getHref={getAppStoreDownloadUrl}
          dataId="AppleAppStoreBadge"
        >
          <AppleAppStoreBadge lang={lang} />
        </AppStoreButtonComponent>
      )}
      {showAndroidButton && (
        <AppStoreButtonComponent
          onClick={handleGetAppClick}
          getHref={getPlayStoreDownloadUrl}
          dataId="GooglePlayBadge"
        >
          <GooglePlayBadge lang={lang} />
        </AppStoreButtonComponent>
      )}
    </Box>
  )
}
